import { useShift } from '@/hooks/shift/useShift';
import { selectCurrency } from '@/redux/config/configSlice';
import { useAppSelector } from '@/redux/hooks';
import { Button, Input, message, Typography } from 'antd';
import dayjs from 'dayjs';
import { join, size } from 'lodash';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

interface ShiftFormProps {
  onStartShift: () => void;
}

const ShiftForm: FC<ShiftFormProps> = ({ onStartShift }) => {
  const [amount, setAmount] = useState<string>('');
  const currency = useAppSelector(selectCurrency);
  const { start } = useShift();
  const { t } = useTranslation();

  const handleChangeAmount = (value: string) => {
    const decimal = Number(value.replace(/\D/g, '')) / 100;
    const { format: formatCurrency } = Intl.NumberFormat('en-US', {
      currency: 'USD',
      style: 'currency',
    });
    setAmount(formatCurrency(decimal || 0).replace('$', ''));
  };

  const handleOpenShift = () => {
    start({
      ID: uuidv4(),
      startAmount: Number(amount.replace(',', '')),
      startDate: dayjs().toISOString(),
    })
      .then(() => {
        onStartShift?.();
      })
      .catch((err) => {
        if (size(err.errors) > 0) {
          message.error(join(err.errors, ','));
        }
      });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <Typography.Title level={3} className="!mb-0">
          {t('common:open_shift_title')}
        </Typography.Title>
        <Typography.Text className="text-base">{t('common:open_shift_desc')}</Typography.Text>
      </div>
      <div className="flex flex-col gap-2.5">
        <Typography.Text className="text-sm text-gray-500">{t('common:amount')}</Typography.Text>
        <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-4">
          <span className="font-medium text-gray-500">{currency}</span>
          <Input
            placeholder="0.00"
            className="border-0 px-0 flex-1"
            value={amount}
            autoFocus
            onChange={(e) => handleChangeAmount(e.target.value)}
          />
        </div>
      </div>
      <Button type="primary" size="large" className="w-full" onClick={handleOpenShift}>
        {t('common:open_shift_btn_title')}
      </Button>
    </div>
  );
};

export default ShiftForm;
